import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import Icon from '@bonprix/pattern-library/templates/icon';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Messages from '@core/scripts/components/messages';
import {Constants} from '../view-constants';
import {articleOverviewStaticContext} from '../ArticleOverviewStatic.jsx';
import FilterGroupContent from '../filter/FilterGroupContent.jsx';
import {Accordion, Button} from '@bonprix/design-system';
import JSONParser from '@core/scripts/helper/json-parser';

import {
    loadOffFilter, loadOn,
    loadOnAccordeon,
    loadOnFilter,
    selectFilterLoadingbarStatus,
    selectLoadingbarStatus,
} from '../LoaderSlice.jsx';
import {useDispatch, useSelector} from 'react-redux';
import {mergeSelectedFiltersWithParams, yieldToMain} from '../ArticleOverviewHelper.js';
import {setFilterAjaxCall} from '../AjaxCallSlice.jsx';
import {loadData} from '../helper/data-loader.js';
import {closeFilter} from '../FilterSlice.jsx';
import {useTrinity} from '../hooks/use-trinity.js';
import {useChangeFilters} from "@article-overview/components/hooks/use-change-filters.js";

function FilterGroups({hits, filterGroups, filterSubmit}) {
    const {texts, browser, filterAjaxUrl, shopId, currentSearchParams} = useContext(articleOverviewStaticContext);
    const contentLoading = useSelector(selectLoadingbarStatus).status;
    const filterLoading = useSelector(selectFilterLoadingbarStatus).status;

    const [isMounted, setIsMounted] = useState(false);
    const accordionWrapper = useRef(null);
    const filterGroup = useRef(null);
    const currentFilters = useRef(new Map());
    const activeIdentifiers = useRef([]);
    const submitButtonWrapper = useRef(null);
    const {resetFilters} = useChangeFilters()

    const dispatch = useDispatch();
    const {sendClickBasic} = useTrinity();

    useEffect(() => {
        setTimeout(() => {
            setIsMounted(true);
        }, 0);
        MessageBroker.publish(Messages.DOM.CHANGED);
    }, []);

    const isIphone =
        browser === Constants.SAFARI_BROWSER && navigator.userAgent.indexOf(Constants.USER_AGENT_IPHONE) !== -1;
    const groupsStyle = isIphone ? {height: window.innerHeight} : {};

    const submitButtonClick = useCallback(
        (event) => {
            yieldToMain(() => {
                event.preventDefault();
                const submitButtonDisabled = event.currentTarget?.getAttribute(Constants.DISABLE_ATTRIBUTE_NAME);
                if (!submitButtonDisabled) {
                    dispatch(loadOnAccordeon());
                    filterSubmit(currentFilters.current);
                }
            });
        },
        [filterSubmit, currentFilters.current],
    );

    const resetFilterClick = () => {
        yieldToMain(() => dispatch(loadOn(true)));
        resetFilters(false)
        sendClickBasic('reset_filter', Constants.FILTER_VIEW_EVENT_VERSION_TESTGROUP);
    }

    const reloadOnlyFilters = async (clickedFilters, isRangeFilter) => {
        const mergedParams = mergeSelectedFiltersWithParams(clickedFilters, currentSearchParams.current);
        const shopIdFormatted = shopId.current ? `/${shopId.current}/` : '/';
        const filterAjaxCall = filterAjaxUrl + shopIdFormatted + '?' + mergedParams.toString();

        dispatch(setFilterAjaxCall([filterAjaxCall, isRangeFilter]));
    };

    const filterOptionChange = useCallback(
        async (identifier, trackingId, clickedFilters, isRange = false) => {
            dispatch(loadOnFilter());
            return await yieldToMain(async () => {
                const filterData = {
                    trackingId: trackingId,
                    clickedFilterOptions: clickedFilters,
                };

                currentFilters.current.set(identifier, filterData);

                if (isRange) {
                    const newHits = await reloadHits(currentFilters.current);
                    if (newHits) {
                        await reloadOnlyFilters(currentFilters.current, isRange);
                    } else {
                        currentFilters.current.delete(identifier);
                        dispatch(loadOffFilter());
                    }
                    return newHits;
                }

                await reloadOnlyFilters(currentFilters.current, isRange);
            });
        },
        [currentFilters.current, reloadOnlyFilters],
    );

    return (
        <div
            ref={filterGroup}
            className="filter__groups -small is-active"
            jsh-filter-groups-small-wrapper=""
            style={groupsStyle}
        >
            {renderHeadline()}
            <div className="filter__headline-line"></div>
            {renderAccordion()}
            {renderSubmitButton()}
        </div>
    );

    function renderHeadline() {
        const handleCloseFilter = () => {
            dispatch(setFilterAjaxCall(['', false]));
            dispatch(closeFilter());
        };
        return (
            <div className="filter__headline-wrapper">
                <div className="filter__headline">
                    <span className="filter__headline-title">{texts.filter_showResult}</span>
                </div>

                <div className="filter__headline-icon" onClick={handleCloseFilter}>
                    <Icon name={'closer'} />
                </div>
            </div>
        );
    }

    function renderAccordion() {
        return (
            <div className="filter__groups-accordion" jsh-accordion-wrapper="" ref={accordionWrapper}>
                <Accordion>
                    {filterGroups.map((filterGroup) => {
                        return renderFilterGroup(filterGroup);
                    })}
                </Accordion>
            </div>
        );
    }

    function saveActiveIdentifier(identifier) {
        const currentState = activeIdentifiers.current.includes(identifier);
        if (!currentState) {
            activeIdentifiers.current.push(identifier);
        } else {
            activeIdentifiers.current = activeIdentifiers.current.filter((item) => item !== identifier);
        }
    }

    function reloadHits(clickedFilters) {
        const shopIdFormatted = shopId.current ? `/${shopId.current}/` : '/';

        const currentParamsCopy = mergeSelectedFiltersWithParams(clickedFilters, currentSearchParams.current);
        const filterAjaxRequest = filterAjaxUrl + shopIdFormatted + '?' + currentParamsCopy.toString();

        const newHitsContent = loadData(filterAjaxRequest);
        return newHitsContent.then((result) => {
            if (result) {
                const modelJson = JSONParser.tryParseJSON(result.model);
                return modelJson.hits;
            }
        });
    }

    function renderFilterGroup(filterGroup) {
        let isActive = activeIdentifiers.current.includes(filterGroup.identifier);
        const withSelection = filterGroup.selectionLabel !== '';
        const label = `${filterGroup.name}${withSelection ? `: ${filterGroup.selectionLabel}` : ''}`;

        return (
            <Accordion.Item
                key={filterGroup.identifier}
                title={label}
                initiallyOpened={isActive}
                onOpen={() => saveActiveIdentifier(filterGroup.identifier)}
                onClose={() => saveActiveIdentifier(filterGroup.identifier)}
            >
                {isMounted && (
                    <FilterGroupContent
                        filterGroup={filterGroup}
                        filterOptionChange={filterOptionChange}
                        preventFilterUpdate={false}
                        mobileSubmitButton={submitButtonWrapper}
                    />
                )}
            </Accordion.Item>
        );
    }

    function renderSubmitButton() {
        return (
            <div className="filter__submit-button-wrapper" test-ref__filter-submit-button="" ref={submitButtonWrapper}>
                <Button
                    onClick={submitButtonClick}
                    variant="primary"
                    loading={contentLoading}
                    disabled={filterLoading || hits === 0}
                    id="filter-submit-button"
                >
                    {texts.filter_submit} ({hits})
                </Button>
                <Button onClick={resetFilterClick} variant="secondary" disabled={filterLoading}>
                    {texts.filter_reset_sidesheet}
                </Button>
            </div>
        );
    }

}

export default React.memo(FilterGroups);
